import { CtaForm } from '@pairy/sheard/types';
import { AnimatePresence } from 'framer-motion';
import { useAtomValue } from 'jotai';
import { WheelEvent, useRef } from 'react';
import throttle from 'lodash.throttle';

import { isCtaModalOpenAtom, prevPageAtom } from '../../../store/index.atom';
import { v2ZoomAtom } from '../../../store/v2/viewer.atom';
import useFitZoom from '../../../hooks/v2/useFitZoom';
import useV2Swipe from '../../../hooks/v2/useV2Swipe';
import usePinchZoom from '../../../hooks/v2/usePinchZoom';
import useDoubleTapZoom from '../../../hooks/v2/useDoubleTapZoom';
import useAnchoredZoom from '../../../hooks/v2/useAnchoredZoom';
import { EmbedItemType, MessageBoxItemType } from '../../PdfViewer/index.types';
import { V2DocumentType } from '../index.types';
import { V2_DOUBLE_TAP_ZOOM, quantizeImageSizes } from '../zoom.constants';
import V2PageImage from '../V2PageImage';
import PageAnnotations from '../PageAnnotations';
import SlideFreeBadge from '../../@common/FreeBadge/Slide';

import * as Styled from './index.styles';

interface Props {
  document: V2DocumentType;
  currentPage: number;
  goToPage: (page: number) => void;
  goToPrevPage: () => void;
  goToNextPage: () => void;
  isNextPageDisabled: boolean;
  blindPages: number[] | null;
  messageBoxItems?: MessageBoxItemType[];
  embedItems?: EmbedItemType[];
  ctaForm?: CtaForm;
  isFormSubmitted?: boolean;
  isMobile: boolean;
}

/** V2 페이지 단위 보기 모드 */
const PagedViewer = ({
  document,
  currentPage,
  goToPage,
  goToPrevPage,
  goToNextPage,
  isNextPageDisabled,
  blindPages,
  messageBoxItems,
  embedItems,
  ctaForm,
  isFormSubmitted,
  isMobile,
}: Props) => {
  const zoom = useAtomValue(v2ZoomAtom);
  const prevPage = useAtomValue(prevPageAtom);
  const isCtaModalOpen = useAtomValue(isCtaModalOpenAtom);

  const contentRef = useRef<HTMLDivElement>(null);
  const pinchLayerRef = useRef<HTMLDivElement>(null);

  const pageData = document.pages[currentPage - 1];
  const prevPageData = document.pages[currentPage - 2];
  const nextPageData = document.pages[currentPage];

  const { renderedWidth, renderedHeight } = useFitZoom({
    originalWidth: pageData?.originalWidth ?? 1,
    originalHeight: pageData?.originalHeight ?? 1,
  });

  const { zoomAtPoint } = useAnchoredZoom({ scrollRef: contentRef });
  usePinchZoom({
    scrollRef: contentRef,
    transformTargetRef: pinchLayerRef,
    zoomAtPoint,
  });
  useDoubleTapZoom({
    scrollRef: contentRef,
    transformTargetRef: pinchLayerRef,
    zoomAtPoint,
  });

  const { handleTouchStart, handleTouchMove, handleTouchEnd } = useV2Swipe({
    totalPage: document.totalPages,
    isNextSwipeDisabled: isNextPageDisabled,
    currentPage,
    onPageChange: (page) => goToPage(page),
    scrollRef: contentRef,
  });

  const navigationRef = useRef({ goToPrevPage, goToNextPage });
  navigationRef.current = { goToPrevPage, goToNextPage };

  /* V1 useWheel과 동일한 감도(500ms 스로틀, ±10px 임계값)로 페이지를 넘긴다 */
  const throttledWheelNavigate = useRef(
    throttle((deltaX: number, deltaY: number) => {
      if (Math.abs(deltaY) <= 10 && Math.abs(deltaX) <= 10) {
        return;
      }
      if (deltaY < 0 || deltaX < 0) {
        navigationRef.current.goToPrevPage();
      } else {
        navigationRef.current.goToNextPage();
      }
    }, 500),
  );

  const handleWheel = (e: WheelEvent<HTMLDivElement>) => {
    /* Ctrl/Cmd + 휠은 useAnchoredZoom의 줌으로 처리한다 */
    if (e.ctrlKey || e.metaKey) {
      return;
    }
    /* 줌 상태나 CTA 모달에서는 네이티브 스크롤/조작에 맡긴다 */
    if (zoom > 1 || isCtaModalOpen) {
      return;
    }
    throttledWheelNavigate.current(e.deltaX, e.deltaY);
  };

  if (!pageData) {
    return null;
  }

  const direction = currentPage >= prevPage ? 1 : -1;
  const slideOffset = isMobile ? 40 * direction : 0;

  return (
    <Styled.ContentArea
      ref={contentRef}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      <Styled.PinchLayer ref={pinchLayerRef}>
        <AnimatePresence mode="popLayout" initial={false}>
          <Styled.MotionPageBox
            key={currentPage}
            data-zoom-anchor
            initial={{ opacity: 0, x: slideOffset }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -slideOffset }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
            style={{ width: renderedWidth, height: renderedHeight }}
          >
            <V2PageImage
              pageData={pageData}
              sizes={quantizeImageSizes(renderedWidth)}
              isBlind={blindPages?.includes(currentPage)}
              loading="eager"
            />
            <PageAnnotations
              pageData={pageData}
              renderedWidth={renderedWidth}
              renderedHeight={renderedHeight}
              messageBoxItems={messageBoxItems}
              embedItems={embedItems}
              ctaForm={ctaForm}
              isFormSubmitted={isFormSubmitted}
              isBlind={blindPages?.includes(currentPage)}
              isMobile={isMobile}
            />
          </Styled.MotionPageBox>
        </AnimatePresence>

        {/*
         * 페이지 전환 애니메이션에 휩쓸리지 않도록 AnimatePresence 밖에 둔다.
         * PinchLayer는 페이지 박스와 크기가 같아 슬라이드 안쪽 기준은 유지된다.
         */}
        <SlideFreeBadge
          userSubscriptionPlan={document.plan}
          isMobile={isMobile}
        />
      </Styled.PinchLayer>

      {/* 인접 페이지 프리로드(비가시) */}
      <Styled.PreloadBox aria-hidden>
        {/*
         * 더블 탭 확대 대비 현재 페이지 고해상도 프리로드.
         * 확대 커밋 시점에야 큰 srcset 후보를 받기 시작하면 저해상도가
         * 확대된 채 잠깐 보이므로, 목표 배율 해상도를 미리 받아둔다.
         */}
        {isMobile && zoom < V2_DOUBLE_TAP_ZOOM && (
          <V2PageImage
            pageData={pageData}
            sizes={quantizeImageSizes(
              (renderedWidth / zoom) * V2_DOUBLE_TAP_ZOOM,
            )}
            loading="eager"
          />
        )}
        {prevPageData && (
          <V2PageImage
            pageData={prevPageData}
            sizes={quantizeImageSizes(renderedWidth)}
            loading="eager"
          />
        )}
        {nextPageData && !isNextPageDisabled && (
          <V2PageImage
            pageData={nextPageData}
            sizes={quantizeImageSizes(renderedWidth)}
            loading="eager"
          />
        )}
      </Styled.PreloadBox>
    </Styled.ContentArea>
  );
};

export default PagedViewer;
